import React, { useEffect, useState } from "react";
import { Loader2, Play, Wand2, AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import api from "@/lib/api";

/**
 * Laaffic → ops_call_rows backfill.
 *
 * Two-phase workflow:
 *   1. Preview (dry-run) — pulls CDRs, resolves each via
 *      voiceGroupId → Campaign.laaffic_voice_group_id → brand_id
 *      (Strategy A) and labelNameList → laaffic_disposition_map,
 *      and returns per-bucket counts + sample rows. No writes.
 *   2. Commit — writes `ready` rows to ops_call_rows with
 *      source="laaffic_backfill", idempotent on laaffic_call_id,
 *      and parks unresolved rows in unmapped_cdrs for triage.
 *
 * Backfilled rows carry `enrichment_pending=true` so the nightly
 * MySQL re-enrichment picks them up and computes credit_status
 * against real deposit data.
 */
const BUCKET_TONES = {
  ready: "text-emerald-700",
  duplicate: "text-slate-500",
  unmapped_group: "text-amber-700",
  unmapped_disposition: "text-amber-700",
  missing_datetime: "text-red-600",
};

const BUCKET_LABELS = {
  ready: "Ready to insert",
  duplicate: "Already ingested",
  unmapped_group: "Unmapped Agent Group",
  unmapped_disposition: "Unmapped disposition",
  missing_datetime: "Missing datetime / callId",
};

export default function LaafficBackfillPage() {
  const [teams, setTeams] = useState([]);
  const [form, setForm] = useState({
    team_id: "",
    date_from: _defaultFrom(),
    date_to: _defaultTo(),
    task_id: "",
    max_records: 30000,
  });
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [err, setErr] = useState("");
  const [committedResult, setCommittedResult] = useState(null);

  useEffect(() => {
    api.get("/teams").then((r) => setTeams(r.data || [])).catch(() => {});
  }, []);

  const bind = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const run = async (dry_run) => {
    if (dry_run) { setPreviewing(true); setPreview(null); setCommittedResult(null); }
    else setCommitting(true);
    setErr("");
    try {
      const body = {
        team_id: form.team_id,
        date_from: form.date_from,
        date_to: form.date_to,
        task_id: form.task_id.trim() || null,
        max_records: Number(form.max_records) || 30000,
        dry_run,
      };
      const { data } = await api.post("/admin/integrations/laaffic/backfill", body);
      if (dry_run) setPreview(data);
      else setCommittedResult(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setPreviewing(false);
      setCommitting(false);
    }
  };

  const s = preview?.stats || committedResult?.stats;
  const samples = preview?.samples || committedResult?.samples;
  const committed = !!committedResult;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="laaffic-backfill-page">
      <div>
        <h1 className="text-2xl font-outfit font-semibold text-slate-900">Laaffic backfill</h1>
        <div className="text-sm text-slate-600 mt-1 max-w-3xl">
          Pull historical Laaffic CDRs and write them to <code className="mx-1 px-1 py-0.5 bg-slate-100 rounded text-xs">ops_call_rows</code>{" "}
          with <code className="mx-1 px-1 py-0.5 bg-slate-100 rounded text-xs">source=&quot;laaffic_backfill&quot;</code>. Always run{" "}
          <b>Preview</b> first — no writes happen until you hit <b>Commit</b>. Idempotent on <code>laaffic_call_id</code>.
        </div>
        <div className="mt-2 flex gap-2 text-xs">
          <Link to="/admin/integrations" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Credentials</Link>
          <Link to="/admin/laaffic-parity" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Parity</Link>
          <Link to="/admin/laaffic-dispositions" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Disposition mapping</Link>
          <Link to="/admin/laaffic-unmapped" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Unmapped queue</Link>
        </div>
      </div>

      <ArchiveDangerZone />
      <div className="glass-card p-4 space-y-3" data-testid="backfill-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500">Team *</label>
            <select required value={form.team_id} onChange={bind("team_id")}
                    className="glass-input w-full h-9 mt-1" data-testid="backfill-team">
              <option value="">— pick a team —</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500">From</label>
            <input type="date" value={form.date_from} onChange={bind("date_from")}
                   className="glass-input w-full h-9 mt-1" data-testid="backfill-from" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500">To</label>
            <input type="date" value={form.date_to} onChange={bind("date_to")}
                   className="glass-input w-full h-9 mt-1" data-testid="backfill-to" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-slate-500">Task ID (optional)</label>
            <input value={form.task_id} onChange={bind("task_id")}
                   className="glass-input w-full h-9 mt-1 font-mono text-xs"
                   placeholder="filter to one campaign"
                   data-testid="backfill-task" />
          </div>
        </div>

        <div className="flex gap-2 pt-1">
          <Button disabled={previewing || committing || !form.team_id}
                  onClick={() => run(true)} variant="outline" data-testid="backfill-preview">
            {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Preview (dry-run)
          </Button>
          <Button disabled={committing || previewing || !preview || !s?.ready}
                  onClick={() => run(false)} data-testid="backfill-commit">
            {committing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            Commit {preview?.stats?.ready ? `(${preview.stats.ready} rows)` : ""}
          </Button>
          {preview && !committedResult && !s?.ready && (
            <div className="text-xs text-slate-500 self-center">Nothing to commit.</div>
          )}
        </div>
      </div>

      {err && (
        <div className="glass-card p-3 text-sm text-red-700 border-l-4 border-red-500" data-testid="backfill-err">{err}</div>
      )}

      {committed && (
        <div className="glass-card p-3 border-l-4 border-emerald-500 text-sm text-emerald-900" data-testid="backfill-commit-success">
          <div className="inline-flex items-center gap-2 font-semibold"><CheckCircle2 className="w-4 h-4" /> Commit complete</div>
          <div className="mt-1 text-xs">
            Inserted <b>{s?.inserted_ops || 0}</b> ops_call_rows and parked <b>{s?.inserted_unmapped || 0}</b> unmapped CDRs.
            {s?.inserted_unmapped > 0 && (
              <> Head to the{" "}
                <Link to="/admin/laaffic-unmapped" className="underline">unmapped queue</Link> to triage them.</>
            )}
          </div>
        </div>
      )}

      {s && (
        <div className="space-y-4" data-testid="backfill-results">
          {(preview?.hit_cap || committedResult?.hit_cap) && (
            <div className="glass-card p-3 text-xs bg-amber-50 border-l-4 border-amber-400 text-amber-900 inline-flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <div>
                <b>Hit safety cap</b> ({(preview?.max_records || committedResult?.max_records || 0).toLocaleString()} CDRs).
                Laaffic has more data for this window than we pulled — narrow the date range and re-run to import the rest.
              </div>
            </div>
          )}
          {(preview?.fetch_error || committedResult?.fetch_error) && (
            <div className="glass-card p-3 text-xs bg-amber-50 border-l-4 border-amber-400 text-amber-800 inline-flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <div>
                <b>Laaffic returned an error:</b>{" "}
                {preview?.fetch_error || committedResult?.fetch_error}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
            <MiniStat label="Pulled" value={s.pulled} />
            <MiniStat label={BUCKET_LABELS.ready} value={s.ready} tone="ok" />
            <MiniStat label={BUCKET_LABELS.duplicate} value={s.duplicate} />
            <MiniStat label={BUCKET_LABELS.unmapped_group} value={s.unmapped_group} tone={s.unmapped_group ? "warn" : undefined} />
            <MiniStat label={BUCKET_LABELS.unmapped_disposition} value={s.unmapped_disposition} tone={s.unmapped_disposition ? "warn" : undefined} />
            <MiniStat label={BUCKET_LABELS.missing_datetime} value={s.missing_datetime} tone={s.missing_datetime ? "bad" : undefined} />
          </div>

          {samples && Object.keys(samples).map((bucket) =>
            samples[bucket]?.length ? (
              <details key={bucket} className="glass-card p-3">
                <summary className={`cursor-pointer text-sm font-medium ${BUCKET_TONES[bucket] || "text-slate-700"}`}>
                  {BUCKET_LABELS[bucket] || bucket} — {samples[bucket].length} sample{samples[bucket].length === 1 ? "" : "s"}
                </summary>
                <SamplesTable bucket={bucket} rows={samples[bucket]} />
              </details>
            ) : null
          )}
        </div>
      )}
    </div>
  );
}

function SamplesTable({ bucket, rows }) {
  const showReason = bucket !== "ready";
  return (
    <div className="overflow-auto mt-2">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-500 text-left">
          <tr>
            <th className="px-2 py-1.5">When (IST)</th>
            <th className="px-2 py-1.5">Customer / phone</th>
            <th className="px-2 py-1.5">Task</th>
            <th className="px-2 py-1.5">Label / disposition</th>
            <th className="px-2 py-1.5">Brand</th>
            {showReason && <th className="px-2 py-1.5">Reason</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td className="px-2 py-1 font-mono text-[10px]">{r.attempt_datetime || "—"}</td>
              <td className="px-2 py-1">
                <div className="font-medium">{r.customer_name || "—"}</div>
                {r.phone_number && <div className="text-[10px] text-slate-500 font-mono">{r.phone_number}</div>}
              </td>
              <td className="px-2 py-1 font-mono text-[10px]">{r.laaffic_task_id || "—"}</td>
              <td className="px-2 py-1">
                <div className="text-[10px] font-mono text-slate-500">{r.laaffic_label_raw || "—"}</div>
                {r.disposition_name && <div className="font-medium">{r.disposition_name}</div>}
              </td>
              <td className="px-2 py-1 tabular-nums">{r.brand_id ?? "—"}</td>
              {showReason && (
                <td className="px-2 py-1 text-[10px] text-slate-600 max-w-md">{r._reason || "—"}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  const cls = tone === "ok" ? "text-emerald-700"
           : tone === "warn" ? "text-amber-700"
           : tone === "bad" ? "text-red-600"
           : "text-slate-900";
  return (
    <div className="glass-card p-2" data-testid={`backfill-stat-${label.replace(/\W+/g, "-").toLowerCase()}`}>
      <div className="text-[9px] uppercase tracking-wide text-slate-500 truncate" title={label}>{label}</div>
      <div className={`text-base font-semibold tabular-nums ${cls}`}>{value ?? 0}</div>
    </div>
  );
}

function _defaultFrom() {
  return "2026-08-11";  // per plan — Excel uploads stopped Aug 10
}
function _defaultTo() {
  return new Date().toISOString().slice(0, 10);
}


/** Danger zone: rename current `ops_call_rows` to a timestamped
 * archive collection, wipe derived caches, and expose a restore
 * button in case we need to undo. One-time operation before the
 * first Laaffic backfill so dashboards start from a clean slate.
 */
function ArchiveDangerZone() {
  const [status, setStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [expanded, setExpanded] = React.useState(false);
  const [running, setRunning] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState("");
  const [confirm, setConfirm] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/integrations/ops-archive-status");
      setStatus(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const runArchive = async () => {
    if (confirm !== "ARCHIVE-AND-WIPE") return;
    setRunning(true); setErr(""); setResult(null);
    try {
      const { data } = await api.post("/admin/integrations/ops-archive-and-wipe", {
        confirm,
        expected_row_count: status?.current_row_count,
        also_clear_daily_agg: true,
        also_clear_snapshots: true,
      });
      setResult(data);
      setConfirm("");
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setRunning(false); }
  };

  const runRestore = async (col) => {
    if (!window.confirm(`Restore ${col} back to ops_call_rows? This will fail if any Laaffic rows have been ingested.`)) return;
    setRunning(true); setErr(""); setResult(null);
    try {
      const { data } = await api.post(
        `/admin/integrations/ops-archive-restore?archive_collection=${encodeURIComponent(col)}`
      );
      setResult({ restored: true, ...data });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setRunning(false); }
  };

  const current = status?.current_row_count ?? 0;

  return (
    <div className="glass-card p-4 border-l-4 border-red-500" data-testid="archive-danger-zone">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <div className="font-outfit font-semibold text-red-800">Danger zone — archive Excel data</div>
            <div className="text-xs text-slate-600 mt-0.5 max-w-2xl">
              Before the first Laaffic backfill, rename the current{" "}
              <code className="px-1 py-0.5 bg-slate-100 rounded text-[10px]">ops_call_rows</code>{" "}
              (Excel-derived) to a timestamped archive so dashboards start clean.
              Reversible via the restore button below.
            </div>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => setExpanded((v) => !v)}
                data-testid="archive-toggle">
          {expanded ? "Hide" : "Show"}
        </Button>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3">
          {loading && <div className="text-xs text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading current state…</div>}

          {status && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div className="glass-card p-2">
                <div className="text-[9px] uppercase tracking-wide text-slate-500">Current rows</div>
                <div className="text-lg font-semibold tabular-nums">{current.toLocaleString()}</div>
              </div>
              <div className="glass-card p-2">
                <div className="text-[9px] uppercase tracking-wide text-slate-500">Date range</div>
                <div className="text-xs font-mono">
                  {status.date_range?.min || "—"} → {status.date_range?.max || "—"}
                </div>
              </div>
              <div className="glass-card p-2">
                <div className="text-[9px] uppercase tracking-wide text-slate-500">Daily agg rows</div>
                <div className="text-lg font-semibold tabular-nums">{(status.will_also_clear?.main_daily_agg || 0).toLocaleString()}</div>
              </div>
              <div className="glass-card p-2">
                <div className="text-[9px] uppercase tracking-wide text-slate-500">Snapshot cache</div>
                <div className="text-lg font-semibold tabular-nums">{(status.will_also_clear?.snapshot_cache || 0).toLocaleString()}</div>
              </div>
            </div>
          )}

          {status?.sources && Object.keys(status.sources).length > 0 && (
            <div className="text-xs">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">By source</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(status.sources).map(([src, n]) => (
                  <span key={src} className="px-2 py-0.5 rounded-full bg-slate-100 font-mono text-[10px]">
                    {src}: <b className="tabular-nums">{n.toLocaleString()}</b>
                  </span>
                ))}
              </div>
            </div>
          )}

          {current > 0 && (
            <div className="glass-card p-3 bg-red-50/40 border border-red-200 space-y-2">
              <div className="text-xs text-red-900">
                Type <code className="px-1 py-0.5 bg-white rounded font-mono text-[10px]">ARCHIVE-AND-WIPE</code> to enable the button:
              </div>
              <input
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="type here to unlock"
                className="glass-input w-full h-9 font-mono text-xs"
                data-testid="archive-confirm-input"
              />
              <Button
                disabled={running || confirm !== "ARCHIVE-AND-WIPE"}
                onClick={runArchive}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="archive-run"
              >
                {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
                Archive {current.toLocaleString()} rows & wipe caches
              </Button>
            </div>
          )}

          {current === 0 && (
            <div className="text-xs text-slate-500 italic">
              ops_call_rows is already empty — nothing to archive.
            </div>
          )}

          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="archive-err">
              {err}
            </div>
          )}

          {result && !result.restored && (
            <div className="text-xs text-emerald-900 bg-emerald-50 border border-emerald-200 rounded p-3">
              <div className="font-semibold inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Archive complete</div>
              <div className="mt-1">
                Archived <b>{(result.archived_row_count || 0).toLocaleString()}</b> rows →{" "}
                <code className="font-mono">{result.archive_collection}</code>. Cleared{" "}
                {(result.cleared_main_daily_agg || 0).toLocaleString()} daily-agg rows and{" "}
                {(result.cleared_snapshot_cache || 0).toLocaleString()} snapshot rows.
              </div>
            </div>
          )}

          {result && result.restored && (
            <div className="text-xs text-emerald-900 bg-emerald-50 border border-emerald-200 rounded p-3">
              Restored <b>{(result.restored_row_count || 0).toLocaleString()}</b> rows from{" "}
              <code className="font-mono">{result.restored_from}</code>.
            </div>
          )}

          {status?.existing_archives?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Previous archives</div>
              <div className="space-y-1">
                {status.existing_archives.map((a) => (
                  <div key={a.collection} className="flex items-center justify-between glass-card p-2 text-xs">
                    <div className="font-mono">{a.collection}</div>
                    <div className="flex items-center gap-3">
                      <span className="tabular-nums text-slate-600">{a.row_count.toLocaleString()} rows</span>
                      <Button size="sm" variant="outline" onClick={() => runRestore(a.collection)}
                              disabled={running || current > 0}
                              title={current > 0 ? "ops_call_rows must be empty to restore" : "Rename this archive back to ops_call_rows"}
                              data-testid={`archive-restore-${a.collection}`}>
                        Restore
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
